// ===========================================
// Fechas de eventos del calendario (hora de pared)
// ===========================================

import { format, parseISO } from 'date-fns'

/**
 * Parsea `eventos_calendario.fecha_inicio` / `.fecha_fin` como hora de pared.
 *
 * La columna es `timestamptz`, pero el front la escribe sin offset (`2026-08-25T00:00:00`),
 * así que Postgres la guarda como UTC y PostgREST la devuelve con `+00:00`. Convertirla con
 * `new Date()` la pasa a hora argentina, resta 3 horas y muestra el día anterior en los
 * eventos de todo el día: eso hacía el detalle mientras la agenda mostraba el día correcto,
 * porque agrupa cortando el string (`calendarioService.obtenerItemsPorDia`).
 *
 * Descartar el offset alinea el render con lo que el usuario tipeó y con el resto del
 * sistema, sin tocar las filas ya escritas.
 *
 * Se corta con regex y no con `slice(0, 19)` para no romperse si el timestamp trae
 * fracción de segundo (`…T14:30:00.5+00:00`), que PostgREST emite cuando existe.
 */
export const parseFechaEvento = (iso: string): Date =>
  parseISO(iso.replace(/(?:Z|[+-]\d{2}:?\d{2})$/, ''))

/**
 * Formatea como HH:MM, en hora argentina, un timestamp que viene de Google Calendar (ORR-301).
 *
 * Es el caso OPUESTO al de `parseFechaEvento`, y confundirlos es el bug. Los timestamps de
 * BrainHub traen un `+00:00` que no significa nada y hay que descartar; los de Google traen
 * un offset REAL, y encima expresado en la zona del calendario del estudio, que no es la de
 * Argentina: medido el 04/09 contra el Workspace, un evento creado a las 15:00 vuelve como
 * `2026-09-12T20:00:00+02:00`.
 *
 * Por eso acá hay que convertir de verdad. Cortar el string —que es lo correcto del otro
 * lado— mostraría "20:00" para una reunión de las 15:00: un aviso de conflicto con el
 * horario equivocado es peor que no avisar, porque se lee como que no hay conflicto.
 */
export const horaArgentinaDesdeGoogle = (iso: string): string =>
  new Date(iso).toLocaleTimeString('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'America/Argentina/Buenos_Aires',
  })

/** Lo mínimo que necesitan los helpers de abajo: sirve para cualquier fila de evento. */
export interface EventoConOcurrencia {
  fecha_inicio: string
  ocurrencia_inicio?: string
  todo_el_dia?: boolean
}

/**
 * Fecha y hora que hay que MOSTRAR de un evento del calendario.
 *
 * En una fila que viene de expandir una serie recurrente, `fecha_inicio` es la del maestro
 * —el ancla de la serie— y la fecha de esa ocurrencia viaja en `ocurrencia_inicio` (ORR-383).
 * Leer `fecha_inicio` pelado muestra la hora del maestro y la fecha equivocada.
 */
export const inicioVisibleEvento = (evento: EventoConOcurrencia): string =>
  evento.ocurrencia_inicio ?? evento.fecha_inicio

/**
 * Hora de un evento como `HH:MM`, o `null` si es de todo el día o la fecha es inválida.
 *
 * `null` significa "no hay hora que mostrar", no "error": quien lo consume decide si pone
 * "Todo el día" o no muestra nada.
 */
export const horaVisibleEvento = (evento: EventoConOcurrencia): string | null => {
  if (evento.todo_el_dia) return null
  try {
    const d = parseFechaEvento(inicioVisibleEvento(evento))
    if (Number.isNaN(d.getTime())) return null
    return format(d, 'HH:mm')
  } catch {
    return null
  }
}
